import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Flex from 'antd/lib/flex';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Steps from 'antd/lib/steps';
import Table from 'antd/lib/table';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    BranchesOutlined,
    CloudOutlined,
    CloudServerOutlined,
    KeyOutlined,
} from '@ant-design/icons';
import { uniq } from 'lodash';
import { OrgUpdatable } from '@/actions/organization';
import { AgentRequest } from '@/actions/orgSettings';
import { clearApiKey } from '@/actions/publicApi';
import { platformLabelForApp } from '@/helpers/mdmPlatformOverride';
import { createAppSettingsIntegrationsRoute } from '@/helpers/routeHelpers';
import { useCreateAgentMutation, useRegenerateAgentAPIKeyMutation } from '@/middlewares/defaultApi';
import { RootState } from '@/reducers/reducers';
import { selectUserAppDataByAppIdMap } from '@/selectors/selectors';
import { Agent, AgentCIKind, AgentType, RunwayResponseType, SubscriptionFeatureType, WebhookEndpoint } from '@/types';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { useRestrictedFeatureIsDisabled } from '../planRestrictions/planRestrictionHelperComponents';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '../styles/globalStyles';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import {
    PickedWorkflows,
    toCIWorkflowsRequest,
    useAgentCIWorkflowOptions,
    useCanRefreshAgentWorkflows,
    useRefreshAgentWorkflows,
    workflowsForAgent,
    WorkflowTable,
} from './AgentCIWorkflowFields';
import { CI_KIND_LABELS, WorkflowSetupLine, WorkflowSetupModal } from './AgentCISetupHelp';
import { AgentKeyContent } from './AgentKeyModal';
import { AgentRoutineSetupModal } from './AgentRoutineSetupHelp';
import { monoStyle, StepBox, StepLine } from './AgentSetupParts';
import {
    agentSampleWorkflowName,
    CLAUDE_ROUTINE_FIRE_URL_PATTERN,
    repoWorkflowLinks,
    isSampleCIKind,
    sampleWorkflowName,
    suggestAgentName,
    suggestedAgentNameBase,
} from './agentSetupHelpers';

const WEBHOOK_URL_PATTERN = /^https:\/\/\S+$/;

enum WizardStep {
    Agent,
    Apps,
    SetUp,
    APIKey,
}

const groupLabel = (text: string, action?: string) => (
    <Space align="center">
        <Text>{text}</Text>
        {action && (
            <TooltipInfoIcon
                tooltipContent={
                    <>
                        This workflow runs{' '}
                        <Text code style={{ color: runwayColors.white }}>
                            {action}
                        </Text>
                    </>
                }
            />
        )}
    </Space>
);

const CI_KIND_OPTIONS = [
    {
        label: groupLabel('Claude Code', 'anthropics/claude-code-action'),
        options: [
            {
                value: AgentCIKind.ClaudeSubscription,
                label: CI_KIND_LABELS[AgentCIKind.ClaudeSubscription],
                short: 'Subscription',
                hint: 'Secret: CLAUDE_CODE_OAUTH_TOKEN',
            },
            {
                value: AgentCIKind.ClaudeApiKey,
                label: CI_KIND_LABELS[AgentCIKind.ClaudeApiKey],
                short: 'API key',
                hint: 'Secret: ANTHROPIC_API_KEY',
            },
        ],
    },
    {
        label: groupLabel('Codex', 'openai/codex-action'),
        options: [
            {
                value: AgentCIKind.CodexApiKey,
                label: CI_KIND_LABELS[AgentCIKind.CodexApiKey],
                short: 'API key',
                hint: 'Secret: OPENAI_API_KEY',
            },
        ],
    },
    {
        label: groupLabel('Other'),
        options: [
            {
                value: AgentCIKind.OwnWorkflow,
                label: CI_KIND_LABELS[AgentCIKind.OwnWorkflow],
                short: 'My own workflow',
                hint: 'Any workflow with the Runway inputs',
            },
        ],
    },
];

const SERVICE_OPTIONS = [
    {
        value: AgentType.ClaudeRoutine,
        label: 'Claude Code routine',
        hint: 'Runway fires a routine you create on claude.ai.',
    },
    {
        value: AgentType.CustomWebhook,
        label: 'Standard webhook',
        hint: "Runway posts each assignment to your service's URL.",
    },
];

const ChoiceCard: React.FC<{
    selected: boolean;
    disabled: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}> = ({ selected, disabled, onClick, icon, title, children }) => (
    <button
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        style={{
            border: `1px solid ${selected && !disabled ? rwColors.primary : antGrey.grey5}`,
            borderRadius: 8,
            padding: '14px 16px',
            background:
                disabled ? antGrey.grey3
                : selected ? colorWithAlpha(rwColors.primary, 0.08)
                : runwayColors.white,
            opacity: disabled && !selected ? 0.5 : 1,
            textAlign: 'left',
            font: 'inherit',
            color: disabled ? antGrey.grey7 : 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            width: '100%',
        }}
    >
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
            {icon}
            {title}
        </span>
        {children}
    </button>
);

const CONSENT_CHECKBOX_ID = 'agent-consent';

const ConsentCheckbox: React.FC<{ checked: boolean; onChange: (checked: boolean) => void; showError: boolean }> = ({
    checked,
    onChange,
    showError,
}) => (
    <Form.Item
        style={{ marginTop: 24, marginBottom: 0 }}
        validateStatus={showError ? 'error' : undefined}
        help={showError ? 'Confirm to create the agent' : undefined}
    >
        <Flex align="center" gap={8}>
            <Checkbox id={CONSENT_CHECKBOX_ID} checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <label htmlFor={CONSENT_CHECKBOX_ID} style={{ cursor: 'pointer' }}>
                I understand that Triage issue data, including crash reports, user feedback, and ticket text will be
                sent to this agent and the service that runs it.
            </label>
        </Flex>
    </Form.Item>
);

// A backend error comes back as an RTK error (see baseQuery), not in data
const mutationErrorMessage = (result: object, fallback: string) => {
    const error = 'error' in result ? result.error : undefined;
    return typeof error === 'object' && error !== null && 'error' in error && typeof error.error === 'string' ?
            error.error || fallback
        :   fallback;
};

interface AgentModalProps {
    orgId: string;
    // Editing opens the same steps, filled in with the agent; its type and CI agent can't change
    agent?: Agent;
    webhook?: WebhookEndpoint;
    otherAgentNames: string[];
    onClose: () => void;
}

const AgentModal: React.FC<AgentModalProps> = ({ orgId, agent, webhook, otherAgentNames, onClose }) => {
    const dispatch = useDispatch();
    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, orgId));
    const webhooksRestricted = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Webhooks);
    const [createAgent, { isLoading: creating }] = useCreateAgentMutation();
    const [regenerateAgentAPIKey, { isLoading: regenerating }] = useRegenerateAgentAPIKeyMutation();
    const [updateAgent, updating, updateResponseType, updateError] = useCallUpdatable(OrgUpdatable.UpdateAgent, {
        orgId,
    });
    const isEditing = !!agent;

    const [step, setStep] = React.useState(WizardStep.Agent);
    const [errorsShown, setErrorsShown] = React.useState(false);
    const [name, setName] = React.useState(agent?.name ?? '');
    const [nameEdited, setNameEdited] = React.useState(isEditing);
    const [isCI, setIsCI] = React.useState(!agent || agent.type === AgentType.CIWorkflow);
    const [ciKind, setCIKind] = React.useState(agent?.ciKind ?? AgentCIKind.ClaudeSubscription);
    const [service, setService] = React.useState(
        agent && agent.type !== AgentType.CIWorkflow ? agent.type : AgentType.ClaudeRoutine,
    );
    const [appIds, setAppIds] = React.useState<string[]>(agent?.appIds ?? []);
    const [picked, setPicked] = React.useState<PickedWorkflows>(agent?.ciWorkflows ?? {});
    const [consent, setConsent] = React.useState(false);
    const [fireUrl, setFireUrl] = React.useState(agent?.routine?.fireUrl ?? '');
    const [token, setToken] = React.useState('');
    const [url, setUrl] = React.useState(webhook?.url ?? '');
    const [apiKey, setApiKey] = React.useState('');
    const [apiKeyWarning, setApiKeyWarning] = React.useState<string>();
    const [showWorkflowSetup, setShowWorkflowSetup] = React.useState(false);
    const [showRoutineSetup, setShowRoutineSetup] = React.useState(false);
    const bodyRef = React.useRef<HTMLDivElement>(null);
    const [firstStepHeight, setFirstStepHeight] = React.useState(0);

    const agentType = isCI ? AgentType.CIWorkflow : service;
    const isWebhook = agentType === AgentType.CustomWebhook;
    const isRoutine = agentType === AgentType.ClaudeRoutine;
    const workflowName =
        !isCI || !isSampleCIKind(ciKind) ? undefined
        : agent ? agentSampleWorkflowName(agent)
        : sampleWorkflowName(name);

    const { options, isLoading, isAnyLoading } = useAgentCIWorkflowOptions(
        orgId,
        appIds,
        !isCI || step === WizardStep.Agent,
    );
    const { refresh, refreshingAppIds } = useRefreshAgentWorkflows(orgId, options);
    const canRefresh = useCanRefreshAgentWorkflows(appIds);

    React.useLayoutEffect(() => {
        if (step === WizardStep.Agent && bodyRef.current && bodyRef.current.offsetHeight !== firstStepHeight) {
            setFirstStepHeight(bodyRef.current.offsetHeight);
        }
    }, [step, isCI, ciKind, service, errorsShown, firstStepHeight]);

    React.useEffect(() => {
        // Only on the first step: after Create the agent list holds the new agent and would rename it
        if (!nameEdited && step === WizardStep.Agent) {
            setName(suggestAgentName(suggestedAgentNameBase(agentType, ciKind), otherAgentNames));
        }
    }, [agentType, ciKind, nameEdited, otherAgentNames, step]);

    const appLabel = (appId: string) => {
        const appData = appDataByAppIdMap[appId];
        return appData ? `${appData.appName} (${platformLabelForApp(appData)})` : appId;
    };

    const nameError =
        !name.trim() ? 'Agent name is required'
        : otherAgentNames.includes(name.trim().toLowerCase()) ? 'An agent with this name already exists'
        : undefined;
    const serviceError =
        !isEditing && isWebhook && webhooksRestricted ? "Webhooks aren't included in your plan" : undefined;
    const appsError = appIds.length === 0 ? 'Select at least one app' : undefined;
    const urlError =
        !url.trim() ? 'Webhook URL is required'
        : !WEBHOOK_URL_PATTERN.test(url.trim()) ? 'URL must start with https://'
        : undefined;
    const fireUrlError =
        !fireUrl ? 'Fire URL is required'
        : !CLAUDE_ROUTINE_FIRE_URL_PATTERN.test(fireUrl) ?
            "Paste the URL of the routine's API trigger: https://api.anthropic.com/v1/claude_code/routines/trig_…/fire"
        :   undefined;
    const tokenError = !isEditing && !token.trim() ? 'Token is required' : undefined;
    const workflows = isCI ? workflowsForAgent(appIds, options, workflowName, picked) : undefined;

    const stepValid: Record<WizardStep, boolean> = {
        [WizardStep.Agent]: !nameError && !serviceError,
        [WizardStep.Apps]: !appsError,
        [WizardStep.SetUp]:
            (consent || isEditing) &&
            (isCI ? !!workflows && !isAnyLoading
            : isWebhook ? !urlError
            : !fireUrlError && !tokenError),
        [WizardStep.APIKey]: true,
    };

    // Each visit to a step starts clean: errors show only once Next or Create is tried there
    const goToStep = (target: WizardStep) => {
        setErrorsShown(false);
        setStep(target);
    };

    const goNext = () => {
        setErrorsShown(true);
        if (stepValid[step]) {
            goToStep(step + 1);
        }
    };

    React.useEffect(() => {
        if (updateResponseType === RunwayResponseType.Success) {
            globalMessage.success('Agent updated.');
            goToStep(WizardStep.APIKey);
        }
    }, [updateResponseType]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    const save = async () => {
        setErrorsShown(true);
        if (!stepValid[WizardStep.SetUp]) {
            return;
        }
        const request: AgentRequest = {
            name: name.trim(),
            appIds,
            ciWorkflows: isCI && workflows ? toCIWorkflowsRequest(workflows, appIds) : undefined,
            routine: isRoutine ? { fireUrl, token: token.trim() } : undefined,
            delivery: isWebhook ? { url: url.trim() } : undefined,
        };
        if (agent) {
            updateAgent({ ...request, agentId: agent.id });
            return;
        }
        const result = await createAgent({
            orgId,
            request: { ...request, type: agentType, ciKind: isCI ? ciKind : undefined, consentAcknowledged: consent },
        });
        const response = 'data' in result ? result.data : undefined;
        if (response?.type === RunwayResponseType.Success && response.data) {
            setApiKey(response.data.apiKey);
            goToStep(WizardStep.APIKey);
            return;
        }
        globalMessage.error(mutationErrorMessage(result, response?.message || 'Error creating agent'));
    };

    const regenerateKey = async () => {
        if (!agent) {
            return;
        }
        const result = await regenerateAgentAPIKey({ orgId, agentId: agent.id });
        const response = 'data' in result ? result.data : undefined;
        if (response?.type === RunwayResponseType.Success && response.data) {
            setApiKey(response.data.apiKey);
            setApiKeyWarning(response.data.warning);
            return;
        }
        globalMessage.error(mutationErrorMessage(result, response?.message || 'Error regenerating API key'));
    };

    const close = () => {
        if (apiKey) {
            dispatch(clearApiKey());
        }
        onClose();
    };

    const footer =
        step === WizardStep.APIKey ?
            [
                <Button key="done" type="primary" onClick={close}>
                    Done
                </Button>,
            ]
        :   [
                step === WizardStep.Agent ?
                    <Button key="cancel" onClick={close}>
                        Cancel
                    </Button>
                :   <Button key="back" icon={<ArrowLeftOutlined />} onClick={() => goToStep(step - 1)}>
                        Back
                    </Button>,
                step < WizardStep.SetUp ?
                    <Button key="next" type="primary" icon={<ArrowRightOutlined />} iconPosition="end" onClick={goNext}>
                        Next
                    </Button>
                :   <Button
                        key="save"
                        type="primary"
                        icon={isEditing ? undefined : <CloudOutlined />}
                        loading={creating || updating}
                        onClick={save}
                    >
                        {isEditing ? 'Save' : 'Create agent'}
                    </Button>,
            ];

    const appOptions = Object.values(appDataByAppIdMap).map((appData) => {
        const blocked = isWebhook && !!appData.disableWebhooks;
        return {
            value: appData.appId,
            disabled: blocked,
            label: blocked ? `${appLabel(appData.appId)} · webhooks turned off` : appLabel(appData.appId),
        };
    });

    const keyRepos =
        isCI ?
            uniq(appIds.map((appId) => (workflows?.[appId] ? options?.[appId]?.repo : undefined)).filter(Boolean))
        :   [];

    const noIntegrationLink = (appId: string) => (
        <Text type="secondary">
            No GitHub Actions integration.{' '}
            <Typography.Link
                href={createAppSettingsIntegrationsRoute(orgId, appId)}
                target="_blank"
                rel="noopener noreferrer"
            >
                Connect it in the app&apos;s integrations
            </Typography.Link>
        </Text>
    );

    return (
        <Modal
            open
            width={760}
            title={
                isEditing ? 'Edit agent'
                : step === WizardStep.APIKey ?
                    `${name.trim()} is ready`
                :   'Create agent'
            }
            onCancel={close}
            footer={footer}
            maskClosable={false}
        >
            <Steps
                size="small"
                current={step}
                style={{ marginBottom: 28 }}
                items={[{ title: 'Agent' }, { title: 'Apps' }, { title: 'Set up' }, { title: 'API key' }]}
            />
            <div ref={bodyRef} style={step === WizardStep.Agent ? undefined : { minHeight: firstStepHeight }}>
                <Form layout="vertical" onSubmitCapture={(e) => e.preventDefault()}>
                    {step === WizardStep.Agent && (
                        <>
                            <Form.Item
                                label="Name"
                                required
                                style={{ marginBottom: 32 }}
                                validateStatus={errorsShown && nameError ? 'error' : undefined}
                                help={errorsShown ? nameError : undefined}
                            >
                                <Input
                                    placeholder="Claude"
                                    value={name}
                                    onChange={(e) => {
                                        setNameEdited(true);
                                        setName(e.target.value);
                                    }}
                                />
                            </Form.Item>
                            <Form.Item label="How should Runway start the agent?" required>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                        gap: 12,
                                    }}
                                >
                                    <ChoiceCard
                                        selected={isCI}
                                        disabled={isEditing}
                                        onClick={() => setIsCI(true)}
                                        icon={<BranchesOutlined />}
                                        title="CI workflow"
                                    >
                                        <Text type="secondary">
                                            Runway runs a GitHub Actions workflow in each app&apos;s repository.
                                        </Text>
                                    </ChoiceCard>
                                    <ChoiceCard
                                        selected={!isCI}
                                        disabled={isEditing}
                                        onClick={() => setIsCI(false)}
                                        icon={<CloudServerOutlined />}
                                        title="Agent service"
                                    >
                                        <Text type="secondary">
                                            Runway notifies an agent that runs elsewhere, through a webhook or a
                                            dedicated request.
                                        </Text>
                                    </ChoiceCard>
                                </div>
                            </Form.Item>
                            {isCI ?
                                <Form.Item label="Which agent runs in the workflow?" required>
                                    <Select
                                        value={ciKind}
                                        disabled={isEditing}
                                        onChange={setCIKind}
                                        options={CI_KIND_OPTIONS}
                                        optionRender={(option) => (
                                            <Space direction="vertical" size={0}>
                                                <Text>{option.data.short}</Text>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {option.data.hint}
                                                </Text>
                                            </Space>
                                        )}
                                    />
                                </Form.Item>
                            :   <Form.Item
                                    label="Which service?"
                                    required
                                    validateStatus={errorsShown && serviceError ? 'error' : undefined}
                                    help={errorsShown ? serviceError : undefined}
                                >
                                    <Select
                                        value={service}
                                        disabled={isEditing}
                                        onChange={setService}
                                        options={SERVICE_OPTIONS.map((option) =>
                                            option.value === AgentType.CustomWebhook && webhooksRestricted ?
                                                {
                                                    ...option,
                                                    disabled: true,
                                                    hint: "Webhooks aren't included in your plan.",
                                                }
                                            :   option,
                                        )}
                                        optionRender={(option) => (
                                            <div>
                                                <div>{option.data.label}</div>
                                                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal' }}>
                                                    {option.data.hint}
                                                </Text>
                                            </div>
                                        )}
                                    />
                                </Form.Item>
                            }
                        </>
                    )}

                    {step === WizardStep.Apps && (
                        <>
                            <Form.Item
                                label="Apps"
                                required
                                validateStatus={errorsShown && appsError ? 'error' : undefined}
                                help={errorsShown ? appsError : undefined}
                                extra="The agent can only be assigned in these apps, and its API key only works for them."
                            >
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select apps"
                                    optionFilterProp="label"
                                    value={appIds}
                                    onChange={setAppIds}
                                    options={appOptions}
                                />
                            </Form.Item>
                            {isWebhook && appOptions.some((option) => option.disabled) && (
                                <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 16 }}>
                                    Apps with webhooks turned off can&apos;t use a standard webhook agent: Runway drops
                                    their messages.
                                </Text>
                            )}
                            {isCI && appIds.length > 0 && (
                                <Form.Item label="Where the workflow runs">
                                    <div style={{ overflowX: 'auto' }}>
                                        <Table
                                            rowKey="appId"
                                            size="middle"
                                            pagination={false}
                                            className="agent-setup-table"
                                            style={{ minWidth: 480 }}
                                            dataSource={appIds.map((appId) => ({ appId }))}
                                            columns={[
                                                {
                                                    title: 'App',
                                                    key: 'app',
                                                    width: 180,
                                                    render: (_: unknown, { appId }: { appId: string }) =>
                                                        appLabel(appId),
                                                },
                                                {
                                                    title: 'GitHub repository',
                                                    key: 'repo',
                                                    render: (_: unknown, { appId }: { appId: string }) =>
                                                        isLoading(appId) ? <Text type="secondary">Loading…</Text>
                                                        : options?.[appId]?.integrationIds.length ?
                                                            <span style={monoStyle}>{options[appId].repo}</span>
                                                        :   noIntegrationLink(appId),
                                                },
                                            ]}
                                        />
                                    </div>
                                </Form.Item>
                            )}
                        </>
                    )}

                    {step === WizardStep.SetUp && isCI && (
                        <Space direction="vertical" size={20} style={{ width: '100%' }}>
                            <WorkflowSetupLine onSetUp={() => setShowWorkflowSetup(true)} />
                            <WorkflowTable
                                orgId={orgId}
                                appIds={appIds}
                                options={options}
                                isLoading={isLoading}
                                sampleWorkflowName={workflowName}
                                picked={picked}
                                onPick={(appId, workflow) => setPicked({ ...picked, [appId]: workflow })}
                                showErrors={errorsShown}
                                refreshingAppIds={refreshingAppIds}
                                onRefresh={refresh}
                            />
                            {!isEditing && (
                                <ConsentCheckbox
                                    checked={consent}
                                    onChange={setConsent}
                                    showError={errorsShown && !consent}
                                />
                            )}
                        </Space>
                    )}

                    {step === WizardStep.SetUp && isRoutine && (
                        <>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                                Create the routine and its API trigger on claude.ai first.{' '}
                                <Typography.Link onClick={() => setShowRoutineSetup(true)}>
                                    See the setup steps
                                </Typography.Link>
                            </Text>
                            <Form.Item
                                label="Fire URL"
                                required
                                validateStatus={errorsShown && fireUrlError ? 'error' : undefined}
                                help={errorsShown ? fireUrlError : undefined}
                                extra="Shown when you add an API trigger to the routine."
                            >
                                <Input
                                    placeholder="https://api.anthropic.com/v1/claude_code/routines/trig_…/fire"
                                    value={fireUrl}
                                    onChange={(e) => setFireUrl(e.target.value.trim())}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Token"
                                required={!isEditing}
                                validateStatus={errorsShown && tokenError ? 'error' : undefined}
                                help={errorsShown ? tokenError : undefined}
                                extra={
                                    isEditing ?
                                        'Saved. Leave it blank to keep it, or paste a new token.'
                                    :   'Generated with the API trigger. Runway stores it encrypted and never shows it again.'
                                }
                            >
                                <Input.Password
                                    autoComplete="new-password"
                                    visibilityToggle={false}
                                    placeholder={isEditing ? '••••••••••••' : 'sk-ant-oat01-…'}
                                    value={token}
                                    onChange={(e) => setToken(e.target.value)}
                                />
                            </Form.Item>
                            {!isEditing && (
                                <ConsentCheckbox
                                    checked={consent}
                                    onChange={setConsent}
                                    showError={errorsShown && !consent}
                                />
                            )}
                        </>
                    )}

                    {step === WizardStep.SetUp && isWebhook && (
                        <>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>
                                What your service receives
                            </Text>
                            <StepBox style={{ marginBottom: 20 }}>
                                <StepLine number={1}>
                                    <span>
                                        Runway posts an event each time the agent is assigned to or unassigned from a
                                        Triage issue, with <Text code>targetBranch</Text> and{' '}
                                        <Text code>fixBranch</Text>.
                                    </span>
                                </StepLine>
                                <StepLine number={2}>
                                    <span>
                                        Your agent reads the issue from the Runway API with the agent&apos;s API key,
                                        shown at the end.
                                    </span>
                                </StepLine>
                                <StepLine number={3}>
                                    <span>
                                        It pushes the fix to <Text code>fixBranch</Text> and Runway opens the pull
                                        request.
                                    </span>
                                    <Text type="secondary">
                                        Opening its own instead? Put <Text code>{'runway-triage:<issue id>'}</Text> in
                                        the body so Runway links it.
                                    </Text>
                                </StepLine>
                            </StepBox>
                            <Form.Item
                                label="Webhook URL"
                                required
                                validateStatus={errorsShown && urlError ? 'error' : undefined}
                                help={errorsShown ? urlError : undefined}
                                extra="Runway notifies this agent here when it's assigned to or unassigned from a Triage issue."
                            >
                                <Input
                                    placeholder="https://example.com/runway-webhook"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                />
                            </Form.Item>
                            {!isEditing && (
                                <ConsentCheckbox
                                    checked={consent}
                                    onChange={setConsent}
                                    showError={errorsShown && !consent}
                                />
                            )}
                        </>
                    )}

                    {step === WizardStep.APIKey &&
                        (apiKey ?
                            <AgentKeyContent
                                agentType={agentType}
                                apiKey={apiKey}
                                warning={apiKeyWarning}
                                repos={keyRepos}
                            />
                        :   <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Text>
                                    Your changes are saved. The API key hasn&apos;t changed, so the agent keeps working
                                    with the key it already has.
                                </Text>
                                <Text type="secondary">
                                    Runway shows a key only once. If the agent no longer has it, regenerate it: the
                                    current key stops working.
                                </Text>
                                <Popconfirm
                                    title="Create a new API key and revoke the current one? The agent can't read issues until you give it the new key."
                                    okText="Regenerate"
                                    overlayStyle={{ maxWidth: 360 }}
                                    onConfirm={regenerateKey}
                                >
                                    <Button icon={<KeyOutlined />} loading={regenerating}>
                                        Regenerate key
                                    </Button>
                                </Popconfirm>
                            </Space>)}
                </Form>
            </div>
            {showWorkflowSetup && (
                <WorkflowSetupModal
                    ciKind={ciKind}
                    workflowName={workflowName ?? ''}
                    repoLinks={repoWorkflowLinks(appIds, options, workflowName ?? '')}
                    canRefresh={appIds.some((appId) => canRefresh[appId])}
                    onClose={() => setShowWorkflowSetup(false)}
                    onCheck={() => {
                        setShowWorkflowSetup(false);
                        refresh(appIds.filter((appId) => canRefresh[appId]));
                    }}
                />
            )}
            {showRoutineSetup && (
                <AgentRoutineSetupModal
                    title="Set up the routine"
                    width={640}
                    onClose={() => setShowRoutineSetup(false)}
                />
            )}
        </Modal>
    );
};

export default AgentModal;
